/**
 * Network Status Storage Service
 * Manages persistent storage of network status data using IndexedDB with localStorage fallback
 */

import type { NetworkStatusData, StoredNetworkStatus } from '../types/networkStatus';

const STORAGE_KEY = 'arbitreasure_network_status';
const DB_NAME = 'ArbitreasureDB';
const DB_VERSION = 1;
const STORE_NAME = 'networkStatus';
const TTL_MS = 3600000; // 1 hour in milliseconds

class NetworkStatusStorage {
  private dbPromise: Promise<IDBDatabase | null>;

  constructor() {
    this.dbPromise = this.initializeDB();
  }

  /**
   * Initialize IndexedDB
   */
  private async initializeDB(): Promise<IDBDatabase | null> {
    // Check if IndexedDB is available
    if (!('indexedDB' in window)) {
      console.warn('IndexedDB not available, will use localStorage fallback');
      return null;
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error('Failed to open IndexedDB:', request.error);
        resolve(null); // Fallback to localStorage
      };

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        
        // Create object store if it doesn't exist
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: 'id' });
        }
      };
    });
  }

  /**
   * Save network status data to storage
   */
  async save(data: NetworkStatusData): Promise<void> {
    const storedData: StoredNetworkStatus = {
      id: 'network_status',
      data,
      timestamp: Date.now(),
      version: DB_VERSION,
    };

    const db = await this.dbPromise;

    if (db) {
      // Try to save to IndexedDB
      try {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await this.promisifyRequest(store.put(storedData));
        return;
      } catch (error) {
        console.error('Failed to save to IndexedDB:', error);
        // Fall through to localStorage
      }
    }

    // Fallback to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(storedData));
    } catch (error) {
      console.error('Failed to save to localStorage:', error);
      throw new Error('Failed to save network status data');
    }
  }

  /**
   * Load network status data from storage
   */
  async load(): Promise<StoredNetworkStatus | null> {
    const db = await this.dbPromise;

    if (db) {
      // Try to load from IndexedDB
      try {
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        const result = await this.promisifyRequest(store.get('network_status'));
        
        if (result) {
          return result as StoredNetworkStatus;
        }
      } catch (error) {
        console.error('Failed to load from IndexedDB:', error);
        // Fall through to localStorage
      }
    }

    // Fallback to localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as StoredNetworkStatus;
      }
    } catch (error) {
      console.error('Failed to load from localStorage:', error);
    }

    return null;
  }

  /**
   * Check if stored data is stale (older than TTL)
   */
  isStale(timestamp: number): boolean {
    return Date.now() - timestamp > TTL_MS;
  }

  /**
   * Clear all stored network status data
   */
  async clear(): Promise<void> {
    const db = await this.dbPromise;

    if (db) {
      try {
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        await this.promisifyRequest(store.delete('network_status'));
      } catch (error) {
        console.error('Failed to clear IndexedDB:', error);
      }
    }

    // Also clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear localStorage:', error);
    }
  }

  /**
   * Get the TTL in milliseconds
   */
  getTTL(): number {
    return TTL_MS;
  }

  /**
   * Convert IDBRequest to Promise
   */
  private promisifyRequest<T = any>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Migrate data if version changes
   */
  async migrate(oldData: StoredNetworkStatus): Promise<StoredNetworkStatus | null> {
    // Handle version migrations here
    if (oldData.version < DB_VERSION) {
      console.log(`Migrating network status data from version ${oldData.version} to ${DB_VERSION}`);
      
      // Example migration logic (adjust based on actual schema changes)
      // For now, just update the version
      return {
        ...oldData,
        version: DB_VERSION,
      };
    }

    return oldData;
  }
}

// Export singleton instance
export const networkStatusStorage = new NetworkStatusStorage();